// Product-adaptive configuration + qualification engine for the investor loan
// pre-qualification stepper. Adapted from the Simply Approved Mortgages Site
// engine, keyed to this project's DSCR/investor product lineup.

export type ProductKey =
  | "dscr"
  | "bridge"
  | "flip"
  | "construction"
  | "portfolio"
  | "section8"
  | "str"
  | "foreign"
  | "multifamily"
  | "hardmoney"
  | "heloc";

export type LoanPurpose = "Purchase" | "Refinance" | "Cash-out refinance";

export type ScenarioInput = {
  purpose: LoanPurpose;
  price?: number;
  down?: number;
  loanAmount?: number;
  rate?: number;
  term?: number;
  termMonths?: number;
  currentBalance?: number;
  expectedRent?: number;
  arv?: number;
  rehabBudget?: number;
  numberOfUnits?: number;
  foreignCountry?: string;
  // Explicit DSCR underwriting overrides (borrower-supplied)
  uwQualifyingRent?: number;
  uwMonthlyPayment?: number;
  uwMonthlyExpenses?: number;
  // Bridge
  exitStrategy?: "Refinance" | "Sale" | "Undecided";
  asIsValue?: number;
  targetCloseDays?: number;
  // Construction
  landCost?: number;
  hardCostBudget?: number;
  builderIdentified?: "Yes" | "No";
  // Portfolio
  numberOfDoors?: number;
  // Section 8
  hapInPlace?: "Yes" | "No" | "In progress";
  hqsPassing?: "Yes" | "No" | "Unsure";
  section8ContractRent?: number; // total contract rent (landlord collects 100%)
  section8HapPayment?: number;   // government-guaranteed portion (~70%)
  // STR
  strIncomeSource?: "Trailing 12 months" | "AirDNA projection" | "Market comps";
  strJurisdictionAllows?: "Yes" | "No" | "Unsure";
};

export type BorrowerInput = {
  creditBand: string;
  entityType?: "Personal" | "LLC" | "Corporation" | "Partnership";
  experienceLevel?: "First-time" | "1-5 deals" | "6+ deals";
  liquidReserves?: number;
  yearsInvesting?: number;
};

export type ContactInput = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  state: string;
  zip: string;
  address?: string;
};

export type QualStatus = "green" | "yellow" | "red";

export type QualResult = {
  status: QualStatus;
  headline: string;
  reasons: string[];
  nextSteps: string[];
  ficoNote: string;
  estimatedDscr?: number;
  estimatedLtv?: number;
  /** Product-specific summary lines to show under the qual chip. */
  productHighlights?: Array<{ label: string; value: string; tone?: "info" | "warn" | "ok" }>;
};

export type ProductConfig = {
  key: ProductKey;
  label: string;
  sourceLabel: string;
  tagline: string;
  minFico: number;
  maxLtv?: number;
  minDscr?: number;
  scenarioLead: string;
  productSpecificPrompt?: string;
  disqualifiers: string[];
};

const FICO_LOWER: Record<string, number> = {
  "760+": 760,
  "740-759": 740,
  "720-739": 720,
  "700-719": 700,
  "680-699": 680,
  "660-679": 660,
  "620-659": 620,
  "580-619": 580,
  "<580": 500,
};

export function ficoLowerFromBand(band: string): number {
  return FICO_LOWER[band] ?? 660;
}

export const PRODUCTS: Record<ProductKey, ProductConfig> = {
  dscr: {
    key: "dscr",
    label: "DSCR rental loan",
    sourceLabel: "DSCR pre-approval stepper",
    tagline: "No tax returns. Qualify off the property's rent — 1.0+ DSCR typical.",
    minFico: 660,
    maxLtv: 85,
    minDscr: 1.0,
    scenarioLead: "Purchase or refinance a rental. The property's rent qualifies the loan.",
    productSpecificPrompt: "What's the expected monthly rent?",
    disqualifiers: [
      "FICO below 660 (some programs go to 620 with lower LTV)",
      "DSCR below 1.0 without significant reserves or lower LTV",
      "LTV above 80% (purchase) / 75% (cash-out)",
      "Property not eligible: owner-occupied, rural, mobile home",
    ],
  },
  multifamily: {
    key: "multifamily",
    label: "Multifamily loan (5+ units)",
    sourceLabel: "Multifamily pre-approval stepper",
    tagline: "Commercial apartment financing sized off NOI, cap rate, and a 1.20x DSCR.",
    minFico: 680,
    maxLtv: 75,
    minDscr: 1.2,
    scenarioLead: "Purchase or refinance a stabilized 5+ unit apartment building held in an LLC.",
    productSpecificPrompt: "How many units, and what is the total monthly rent roll?",
    disqualifiers: [
      "Fewer than 5 units (use a DSCR or portfolio loan instead)",
      "Portfolio DSCR below 1.20x (1.25x on cash-out)",
      "LTV above 75% (70% cash-out)",
      "Occupancy below 85% without a bridge takeout plan",
      "FICO below 680",
    ],
  },
  hardmoney: {
    key: "hardmoney",
    label: "Hard money / private money loan",
    sourceLabel: "Hard money pre-approval stepper",
    tagline: "Asset-based capital in 5–14 days — condition and vacancy are not disqualifiers.",
    minFico: 620,
    maxLtv: 80,
    scenarioLead: "Short-term, equity-driven financing for acquisitions, rehabs, land, cash-out, and buyouts.",
    productSpecificPrompt: "What is the as-is value, and what is your exit — sale or refinance?",
    disqualifiers: [
      "LTV above 80% of as-is value (70% cash-out, 55% land)",
      "No identified exit — sale or refinance",
      "Owner-occupied or consumer-purpose transaction",
      "Insufficient liquidity to carry 3–6 months of interest",
    ],
  },
  heloc: {
    key: "heloc",
    label: "Investment property HELOC (line of credit)",
    sourceLabel: "HELOC pre-approval stepper",
    tagline: "First-lien DSCR line of credit — draw, repay, and redraw during a 3-year interest-only period.",
    minFico: 700,
    maxLtv: 75,
    minDscr: 1.0,
    scenarioLead: "A revolving first-lien line secured by an investment property, sized off the property's rent.",
    productSpecificPrompt: "What line amount are you targeting, and what is the monthly rent roll?",
    disqualifiers: [
      "FICO below 700 (720 in CT, FL, NJ)",
      "LTV/CLTV above 75% purchase, 70% rate/term, 65% cash-out",
      "DSCR below 1.00 on gross rents",
      "First-time investors — experienced investors only",
      "Line size outside $300,000 – $2,500,000",
    ],
  },
  bridge: {
    key: "bridge",
    label: "Bridge / hard money loan",
    sourceLabel: "Bridge pre-approval stepper",
    tagline: "Fast close, 12–24 month terms — for acquisitions, refis, or transitional deals.",
    minFico: 640,
    maxLtv: 75,
    scenarioLead: "Short-term financing to acquire or reposition a property before a permanent take-out.",
    disqualifiers: [
      "FICO below 640",
      "LTV above 75% (as-is value)",
      "No clear exit strategy (refi or sale)",
    ],
  },
  flip: {
    key: "flip",
    label: "Fix & flip loan",
    sourceLabel: "Fix & flip pre-approval stepper",
    tagline: "Up to 90% purchase + 100% rehab, interest-only, 12-month terms.",
    minFico: 660,
    maxLtv: 90,
    scenarioLead: "Purchase + rehab financing tied to your ARV (after-repair value).",
    productSpecificPrompt: "What's your rehab budget and after-repair value (ARV)?",
    disqualifiers: [
      "FICO below 660",
      "Loan-to-ARV above 75%",
      "No prior flip experience without stronger reserves",
    ],
  },
  construction: {
    key: "construction",
    label: "New construction loan",
    sourceLabel: "Construction pre-approval stepper",
    tagline: "Ground-up construction financing for infill and small subdivision builds.",
    minFico: 680,
    maxLtv: 80,
    scenarioLead: "Interest-only draws during construction, then permanent take-out or sale.",
    productSpecificPrompt: "What's the total project budget (land + hard costs)?",
    disqualifiers: [
      "FICO below 680",
      "Loan-to-cost above 85%",
      "No builder / GC identified",
    ],
  },
  portfolio: {
    key: "portfolio",
    label: "Portfolio rental loan",
    sourceLabel: "Portfolio pre-approval stepper",
    tagline: "Single loan across 5+ doors. Blanket lien, streamlined draws.",
    minFico: 680,
    maxLtv: 75,
    minDscr: 1.15,
    scenarioLead: "Consolidate multiple rentals under one loan with a shared amortization.",
    productSpecificPrompt: "How many doors are in the portfolio?",
    disqualifiers: [
      "Portfolio DSCR below 1.15",
      "Fewer than 5 doors (use single-asset DSCR instead)",
      "FICO below 680",
    ],
  },
  section8: {
    key: "section8",
    label: "Section 8 rental loan",
    sourceLabel: "Section 8 pre-approval stepper",
    tagline: "DSCR loan on Section 8 rentals — priced on HAP + contract rent split.",
    minFico: 660,
    maxLtv: 85,
    minDscr: 1.0,
    scenarioLead: "Landlord collects 100% of contract rent, but only the HAP portion (~70%) is government-guaranteed. We underwrite both bases. Full leverage needs a 1.00 DSCR; the program floor is 0.75 DSCR at reduced leverage (680 FICO, 75% max LTV). Up to 80% LTV — 85% only as an exception at 740+ FICO on an SFR — and reserves as light as zero months on strong low-leverage files.",
    disqualifiers: [
      "FICO below 660",
      "DSCR below 1.00 on the full contract rent (sub-1.0 DSCR programs may be available with compensating factors)",
      "Property not HQS-passing / recently inspected",
    ],

  },
  str: {
    key: "str",
    label: "Airbnb / short-term rental loan",
    sourceLabel: "STR pre-approval stepper",
    tagline: "DSCR using AirDNA or 12-month STR income — not just long-term market rent.",
    minFico: 680,
    maxLtv: 75,
    minDscr: 1.0,
    scenarioLead: "Qualifies off actual short-term rental income (or an AirDNA projection).",
    productSpecificPrompt: "What's the trailing 12-month or projected annual STR revenue?",
    disqualifiers: [
      "FICO below 680",
      "DSCR below 1.0 on STR income",
      "Local jurisdiction bans / severely restricts STRs",
    ],
  },
  foreign: {
    key: "foreign",
    label: "Foreign national loan",
    sourceLabel: "Foreign national pre-approval stepper",
    tagline: "US investment property loan for non-US citizens/residents — no SSN required.",
    minFico: 0, // no US FICO required
    maxLtv: 75,
    scenarioLead: "US property purchase or refi for a foreign national investor.",
    productSpecificPrompt: "What country do you reside in?",
    disqualifiers: [
      "LTV above 75% on purchase, or above 70% on cash-out",
      "No verifiable international credit / bank reference",
      "Property occupancy other than investment",
    ],
  },
};

// -------- Qualification engine (rough, educational only) --------

export function computeQualification(
  product: ProductConfig,
  scenario: ScenarioInput,
  borrower: BorrowerInput,
): QualResult {
  const fico = ficoLowerFromBand(borrower.creditBand);
  const reasons: string[] = [];
  const nextSteps: string[] = [];
  let status: QualStatus = "green";

  const baseLoan =
    scenario.loanAmount ?? Math.max(0, (scenario.price ?? 0) - (scenario.down ?? 0));
  const homeValue = scenario.price ?? 0;
  const ltv = homeValue > 0 ? (baseLoan / homeValue) * 100 : undefined;

  // DSCR-style products: rough DSCR = rent / (P&I + T&I estimate).
  // If the borrower provided explicit underwriting inputs (qualifying
  // rent + estimated mortgage payment + expenses), use those instead.
  let dscr: number | undefined;
  if (["dscr", "portfolio", "section8", "str"].includes(product.key)) {
    const uwRent = scenario.uwQualifyingRent ?? 0;
    const uwPmt = scenario.uwMonthlyPayment ?? 0;
    const uwExp = scenario.uwMonthlyExpenses ?? 0;
    if (uwRent > 0 && uwPmt + uwExp > 0) {
      dscr = uwRent / (uwPmt + uwExp);
    } else if (scenario.expectedRent) {
      const r = (scenario.rate ?? 7.5) / 100 / 12;
      const n = (scenario.term ?? 30) * 12;
      const pi = r === 0 ? baseLoan / n : (baseLoan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
      const escrowGuess = (homeValue * 0.0125) / 12 + 150;
      const monthlyPiti = pi + escrowGuess;
      dscr = monthlyPiti > 0 ? scenario.expectedRent / monthlyPiti : undefined;
    }
  }


  // FICO checks (foreign national exempt)
  if (product.key !== "foreign" && fico < product.minFico) {
    status = "red";
    reasons.push(
      `Your credit tier (${borrower.creditBand}) is below the typical ${product.label} floor of ${product.minFico}.`,
    );
    nextSteps.push("Pull your reports and dispute inaccuracies; ask us about a rapid-rescore plan.");
  } else if (product.key !== "foreign" && fico < product.minFico + 40) {
    if (status === "green") status = "yellow";
    reasons.push(
      `Your credit tier (${borrower.creditBand}) meets the ${product.label} minimum but is tight — pricing improves at the next tier.`,
    );
  }

  // LTV check
  if (product.maxLtv && ltv && ltv > product.maxLtv) {
    status = "red";
    reasons.push(`Loan-to-value ${ltv.toFixed(0)}% exceeds the ${product.label} cap of ${product.maxLtv}%.`);
    nextSteps.push(`Increase down payment to at least ${(100 - product.maxLtv).toFixed(0)}%.`);
  }

  // DSCR check
  if (product.minDscr && dscr != null) {
    if (dscr < product.minDscr) {
      status = "red";
      reasons.push(
        `Estimated DSCR ${dscr.toFixed(2)} is below the ${product.label} floor of ${product.minDscr.toFixed(2)}.`,
      );
      nextSteps.push("Increase expected rent, lower the loan amount, or add reserves.");
    } else if (dscr < product.minDscr + 0.1) {
      if (status === "green") status = "yellow";
      reasons.push(`DSCR ${dscr.toFixed(2)} is close to the ${product.minDscr.toFixed(2)} floor — reserves help.`);
    }
  }

  // ---- Product-specific evaluation ----
  const highlights: NonNullable<QualResult["productHighlights"]> = [];
  const fmtUsd0 = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

  if (product.key === "bridge" || product.key === "hardmoney") {
    if (scenario.asIsValue && scenario.asIsValue > 0) {
      const asIsLtv = (baseLoan / scenario.asIsValue) * 100;
      highlights.push({ label: "LTV vs. as-is value", value: `${asIsLtv.toFixed(0)}%`, tone: asIsLtv > 75 ? "warn" : "ok" });
      if (asIsLtv > 75) {
        status = status === "red" ? "red" : "yellow";
        reasons.push(
          product.key === "hardmoney"
            ? `Loan is ${asIsLtv.toFixed(0)}% of as-is value — private money generally caps at 80%, and pricing improves well below that.`
            : `Loan is ${asIsLtv.toFixed(0)}% of as-is value — bridge lenders typically cap at 75%.`,
        );
      }
    }
    if (scenario.exitStrategy === "Undecided") {
      status = status === "red" ? "red" : "yellow";
      reasons.push("A clear exit — refinance or sale — is required before funding.");
      nextSteps.push("Pick a target exit (refi or sale) and rough timeline.");
    } else if (scenario.exitStrategy) {
      highlights.push({ label: "Exit strategy", value: scenario.exitStrategy, tone: "info" });
    }
    if (scenario.targetCloseDays != null) {
      highlights.push({ label: "Target close", value: `${scenario.targetCloseDays} days`, tone: scenario.targetCloseDays < 14 ? "warn" : "info" });
      if (scenario.targetCloseDays < 10) reasons.push("Sub-10-day closes require appraisal waivers and clean title — call us to pressure-test.");
    }
    if (scenario.termMonths != null) highlights.push({ label: "Loan term", value: `${scenario.termMonths} months`, tone: "info" });
  }

  if (product.key === "multifamily") {
    const units = scenario.numberOfUnits ?? scenario.numberOfDoors;
    if (units != null) {
      highlights.push({ label: "Units", value: `${units}`, tone: units >= 5 ? "ok" : "warn" });
      if (units < 5) {
        status = status === "red" ? "red" : "yellow";
        reasons.push(`${units} units is under the 5-unit commercial threshold — a DSCR or portfolio loan is usually the better fit.`);
        nextSteps.push("Compare a 1–4 unit DSCR loan or a portfolio blanket loan instead.");
      }
      if (units > 50) {
        reasons.push("Above 50 units we underwrite case-by-case with a full sponsor review.");
      }
    }
    if (scenario.expectedRent != null && scenario.expectedRent > 0) {
      const egi = scenario.expectedRent * 12 * 0.94;
      const noi = egi * 0.58;
      highlights.push({ label: "Estimated NOI (illustrative)", value: `${fmtUsd0(noi)} / yr`, tone: "info" });
      nextSteps.push("Send the certified rent roll and T-12 so we can underwrite actual NOI instead of an estimate.");
    }
  }

  if (product.key === "construction") {
    const total = (scenario.landCost ?? 0) + (scenario.hardCostBudget ?? 0);
    if (total > 0) {
      highlights.push({ label: "Total project budget", value: fmtUsd0(total), tone: "info" });
      const ltc = total > 0 ? (baseLoan / total) * 100 : undefined;
      if (ltc != null) {
        highlights.push({ label: "Loan-to-cost (est.)", value: `${ltc.toFixed(0)}%`, tone: ltc > 85 ? "warn" : "ok" });
        if (ltc > 85) {
          status = status === "red" ? "red" : "yellow";
          reasons.push(`Loan-to-cost ${ltc.toFixed(0)}% exceeds the 85% construction cap.`);
        }
      }
    }
    if (scenario.builderIdentified === "No") {
      status = status === "red" ? "red" : "yellow";
      reasons.push("A licensed builder / GC must be identified before construction underwriting.");
      nextSteps.push("Line up a builder and share their license + prior project list.");
    } else if (scenario.builderIdentified === "Yes") {
      highlights.push({ label: "Builder identified", value: "Yes", tone: "ok" });
    }
  }

  if (product.key === "portfolio") {
    if (scenario.numberOfDoors != null) {
      highlights.push({ label: "Doors in portfolio", value: String(scenario.numberOfDoors), tone: scenario.numberOfDoors < 5 ? "warn" : "info" });
      if (scenario.numberOfDoors < 5) {
        status = "red";
        reasons.push("Portfolio loans require 5+ doors — consider a single-asset DSCR loan instead.");
        nextSteps.push("Switch to the DSCR product for a single-property loan.");
      }
    }
  }

  if (product.key === "section8") {
    if (scenario.hapInPlace === "No") {
      status = status === "red" ? "red" : "yellow";
      reasons.push("Without an active HAP contract we underwrite off market rent, not the guaranteed HAP figure.");
    } else if (scenario.hapInPlace) {
      highlights.push({ label: "HAP contract", value: scenario.hapInPlace, tone: scenario.hapInPlace === "Yes" ? "ok" : "info" });
    }
    if (scenario.hqsPassing === "No") {
      status = status === "red" ? "red" : "yellow";
      reasons.push("HUD requires an HQS-passing inspection before HAP payments release.");
      nextSteps.push("Schedule an HQS inspection; we can recommend inspectors.");
    } else if (scenario.hqsPassing) {
      highlights.push({ label: "HQS inspection", value: scenario.hqsPassing, tone: scenario.hqsPassing === "Yes" ? "ok" : "info" });
    }
    // Model DSCR on BOTH bases — guaranteed HAP only (conservative) and full
    // contract rent (aggressive). Landlord receives 100% of contract rent, but
    // only ~70% (HAP) is government-guaranteed; ~30% tenant share is not.
    const contract = scenario.section8ContractRent;
    const hap = scenario.section8HapPayment;
    if (contract != null && hap != null && contract > 0 && dscr != null && scenario.uwQualifyingRent) {
      const baseRent = scenario.uwQualifyingRent;
      if (baseRent > 0) {
        const pitia = baseRent / dscr; // reverse-solve monthly PITIA the DSCR used
        if (pitia > 0) {
          const dscrHap = +(hap / pitia).toFixed(2);
          const dscrContract = +(contract / pitia).toFixed(2);
          highlights.push({ label: "DSCR — full contract rent", value: dscrContract.toFixed(2), tone: dscrContract >= 1.0 ? "ok" : "warn" });
          highlights.push({ label: "DSCR — HAP-only (conservative)", value: dscrHap.toFixed(2), tone: dscrHap >= 1.0 ? "ok" : "warn" });
          if (dscrHap < 1.0 && dscrContract >= 1.0) {
            if (status === "green") status = "yellow";
            reasons.push("HAP-only DSCR is below 1.0 — conservative programs will decline; full-contract-rent programs still price.");
          } else if (dscrContract < 1.0) {
            status = "red";
            reasons.push("Full-contract-rent DSCR is below 1.0 — this file needs more rent, less debt, or a price reduction.");
          }
        }
      }
    }
  }

  if (product.key === "str") {
    if (scenario.strJurisdictionAllows === "No") {
      status = "red";
      reasons.push("Most STR lenders won't fund in jurisdictions that ban or heavily restrict short-term rentals.");
      nextSteps.push("Pivot to a long-term DSCR loan on this property, or pick a permitted market.");
    } else if (scenario.strJurisdictionAllows) {
      highlights.push({ label: "STR permitted", value: scenario.strJurisdictionAllows, tone: scenario.strJurisdictionAllows === "Yes" ? "ok" : "warn" });
    }
    if (scenario.strIncomeSource) {
      highlights.push({
        label: "Income basis",
        value: scenario.strIncomeSource,
        tone: scenario.strIncomeSource === "Trailing 12 months" ? "ok" : "info",
      });
      if (scenario.strIncomeSource === "Market comps") {
        if (status === "green") status = "yellow";
        reasons.push("Comp-based STR income is treated more conservatively than trailing actuals or AirDNA.");
      }
    }
  }

  if (status === "green") reasons.unshift("Your scenario looks like a fit on paper — real terms come from underwriting.");

  const headline =
    status === "green"
      ? "Looks like a fit — let's confirm terms."
      : status === "yellow"
        ? "You're close — a loan officer can shape this into a fit."
        : "This scenario has real hurdles — talk to us about options.";

  return {
    status,
    headline,
    reasons,
    nextSteps,
    ficoNote: `Educational only — real terms come from a full underwrite and appear on your Loan Estimate.`,
    estimatedDscr: dscr,
    estimatedLtv: ltv,
    productHighlights: highlights.length > 0 ? highlights : undefined,
  };
}
